// Raw footage on the Shoot stage (HD-8, on src/ui): the clips linked to this production as a Grid (medium), a preview
// monitor above it for the clip being watched (the old row's native player, same element and attributes), Select mode,
// "Download all" / "Download selected" as one zip, and per clip: faces, Transcribe, Process clips, Download.
// Faces: the eye's people on a clip are named in a dialog of FaceCards (components/FaceNamer, compact), which replaces the
// retired FaceNamerLegacy inline control; the calls are the same (PUT / DELETE /api/footage/:id/faces/:tag, proposals
// POST …/faces/:tag/proposal). F-1 (c): a cold-archived clip (no hot object) cannot play, download, transcribe or process.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Download, Film, Play, Scissors, ScanFace, X } from 'lucide-react';
import api from '../../lib/api';
import { mediaKindOf } from '../../lib/mediaKind';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import Checkbox from '../../ui/Checkbox';
import Grid from '../../ui/Grid';
import Icon from '../../ui/Icon';
import Modal from '../../ui/Modal';
import Tag from '../../ui/Tag';
import { useToast } from '../../ui/Toast';
import { FaceCard, useFaceSave } from '../../components/FaceNamer';
import { clean } from '../../shell/copy';
import { openUrl, plural } from './shared';

const stop = e => e.stopPropagation();

function InStorageTag({ cold }) {
  return (
    <span data-testid="clip-in-storage">
      <Tag title={cold ? 'This clip was archived to Storage. Restore it there (Promote) to download.' : 'Archived with its Posted production. Still playable here.'}>In storage</Tag>
    </span>
  );
}

// The faces dialog for one clip: a FaceCard per person tag, one shared saver (Saved / Undo end when the dialog closes).
function FacesDialog({ clip, cast, chars, onFaces, onClose }) {
  const sv = useFaceSave(clip.id, onFaces);
  const close = () => { sv.reset(); onClose(); };
  return (
    <Modal title="Name the faces" meta={clean(clip.fileName)} size="medium" onClose={close} testId="footage-face-popover">
      <div className="ui-stack">
        {(clip.visualFaces || []).map(f => (
          <FaceCard key={f.tag} face={f} footageId={clip.id} cast={cast} chars={chars} canName={!!clip.canNameFaces} onFaces={onFaces} saver={sv} compact />
        ))}
      </div>
    </Modal>
  );
}

export default function FootageList({ rawFootage, playingClipId, setPlayingClipId, footageSelectMode, toggleFootageSelectMode, footageDeselected, toggleClipSelected,
  selectedFootage, footageZip, handleFootageZipDownload, adcStartingIds, handleProcessClips, transcribingIds, handleTranscribeClip, canTranscribe, faceCast, faceChars, onClipFaces }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [facesFor, setFacesFor] = useState(null);   // clip id whose faces dialog is open

  if (rawFootage.length === 0) {
    return (
      <section className="ui-section" aria-label="Raw footage">
        <h2 className="ui-section__title">Raw footage</h2>
        <p className="ui-p ui-dense ui-text-2">No raw footage is linked to this production yet. Upload it on the Raw footage page and match it here.</p>
        <span><Button variant="secondary" icon={Film} onClick={() => navigate('/footage')}>Open raw footage</Button></span>
      </section>
    );
  }

  const playing = rawFootage.find(c => c.id === playingClipId && c.playbackUrl) || null;
  const togglePlay = clip => { if (clip.playbackUrl) setPlayingClipId(prev => (prev === clip.id ? null : clip.id)); };
  async function downloadClip(clip) {
    try { const r = await api.get(`/api/footage/${clip.id}/download`); openUrl(r.data.url); }
    catch { toast('The clip did not download. Try again.', { tone: 'danger' }); }
  }
  const facesClip = facesFor ? rawFootage.find(c => c.id === facesFor) : null;

  const columns = [
    { key: 'pick', label: 'Pick', width: 56, hide: !footageSelectMode, render: clip => {
      const cold = !!clip.archivedToArchiveId;
      return (
        <span onClick={stop}>
          {cold
            ? <Checkbox label={<span className="ui-sr">In storage: cannot be downloaded</span>} checked={false} disabled readOnly title="In storage: this clip cannot be included in the download" />
            : <Checkbox label={<span className="ui-sr">Include {clip.fileName}</span>} checked={!footageDeselected.has(clip.id)} onChange={() => toggleClipSelected(clip.id)} title="Include this clip in the download" />}
        </span>
      );
    } },
    { key: 'thumb', label: 'Clip', width: 72, render: clip => (clip.thumbnailUrl
      ? <img className="ui-thumb" src={clip.thumbnailUrl} alt="" />
      : <span className="ui-thumb ui-thumb--label">{mediaKindOf(clip) === 'audio' ? 'Audio' : 'Clip'}</span>) },
    { key: 'file', label: 'File', width: 300, render: clip => (
      <span className="ui-stack ui-stack--tight ui-clip" title={clip.playbackUrl ? (playingClipId === clip.id ? 'Hide the player' : 'Watch this clip') : clip.archivedToArchiveId ? 'In storage: no hot copy to play here' : 'This clip is not ready to play yet'}>
        <span className="ui-cluster ui-cluster--nowrap">
          {clip.parentFootageId && <Icon icon={Scissors} label="Scene clip" />}
          <span className="ui-truncate">{clip.fileName}</span>
          {clip.clipCount > 0 && <Tag>{plural(clip.clipCount, 'clip')}</Tag>}
          {clip.isArchived && <InStorageTag cold={!!clip.archivedToArchiveId} />}
        </span>
        {(clip.sceneLabel || clip.clientName) && <span className="ui-meta ui-truncate">{clean(clip.sceneLabel || clip.clientName)}</span>}
      </span>
    ) },
    { key: 'faces', label: 'Faces', width: 128, render: clip => {
      const faces = clip.visualFaces || [];
      if (!clip.visualIndexed || !faces.length) return null;
      return (
        <span data-testid="footage-face-inline" data-count={faces.length} onClick={stop}>
          <Button variant="quiet" icon={ScanFace} data-testid="footage-face-inline-more" onClick={() => setFacesFor(clip.id)} title="The eye's people in this clip">Faces ({faces.length})</Button>
        </span>
      );
    } },
    { key: 'tx', label: 'Transcript', width: 152, render: clip => {
      if (clip.parentFootageId) return null;
      const ts = clip.transcriptionStatus; const starting = transcribingIds?.has(clip.id); const cold = !!clip.archivedToArchiveId;
      if (ts === 'complete') return <span data-testid="clip-tx-ready"><Badge tone="ok">Transcript</Badge></span>;
      if (ts === 'pending' || ts === 'processing') return <span data-testid="clip-tx-running"><Badge tone="info">Transcribing</Badge></span>;
      if (!canTranscribe || cold) return null;   // F-1 (c): nothing to transcribe once the hot object is gone
      return (
        <span onClick={stop}>
          <Button variant="secondary" data-testid="clip-transcribe" loading={starting} title={ts === 'failed' ? 'Retry the transcription' : 'Transcribe this clip (needed for the scrub and clip matching)'}
            onClick={() => handleTranscribeClip(clip.id)}>{ts === 'failed' ? 'Retry transcribe' : 'Transcribe'}</Button>
        </span>
      );
    } },
    { key: 'adc', label: 'Scene clips', width: 160, render: clip => {
      if (clip.parentFootageId || mediaKindOf(clip) === 'audio') return null;
      const st = clip.adcStatus;
      if (st === 'pending' || st === 'processing') return <Badge tone="info">Processing</Badge>;
      if (st === 'complete') return <span className="ui-meta" title="Scenes already extracted">Clips extracted</span>;
      if (clip.archivedToArchiveId) return null;   // F-1 (c): Process clips reads the hot object
      const txReady = clip.transcriptionStatus === 'complete'; const starting = adcStartingIds.has(clip.id);
      return (
        <span onClick={stop}>
          <Button variant="quiet" icon={Scissors} disabled={!txReady} loading={starting} title={txReady ? 'Extract scene clips from this footage' : 'Transcribe this footage first'}
            onClick={() => handleProcessClips(clip.id)}>Process clips</Button>
        </span>
      );
    } },
    { key: 'dl', label: 'Download', width: 88, render: clip => (clip.archivedToArchiveId ? null : (
      <span onClick={stop}><Button variant="quiet" iconOnly icon={Download} label={`Download ${clip.fileName}`} data-testid="footage-download" onClick={() => downloadClip(clip)} /></span>
    )) },
  ];
  const rows = rawFootage;
  const height = Math.min(520, 32 + rows.length * 40 + 2);

  return (
    <section className="ui-section" data-testid="raw-footage" aria-label="Raw footage">
      <div className="ui-section__head ui-section__head--center">
        <h2 className="ui-section__title">
          <button type="button" className="ui-disclosure" aria-expanded={open} onClick={() => setOpen(v => !v)}><Icon icon={ChevronDown} />Raw footage</button>
        </h2>
        <span className="ui-meta">{plural(rawFootage.length, 'clip')}</span>
        <span className="ui-section__actions">
          <span title="Pick which clips to download">
            <Checkbox label="Select" checked={footageSelectMode} onChange={e => { toggleFootageSelectMode(e.target.checked); if (e.target.checked) setOpen(true); }} />
          </span>
          <Button variant="secondary" icon={Download} data-testid="footage-zip" loading={footageZip?.status === 'processing'} disabled={selectedFootage.length === 0}
            title={selectedFootage.length === 0 ? 'Every clip here is in storage. Restore it there (Promote) to download.' : footageSelectMode ? 'Download the selected clips as one zip' : 'Download every clip for this video as one zip (clips in storage are skipped)'}
            onClick={handleFootageZipDownload}>
            {footageZip?.status === 'processing' ? 'Preparing zip' : footageSelectMode ? `Download selected (${selectedFootage.length})` : 'Download all'}
          </Button>
        </span>
      </div>
      {open && playing && (
        <div className="ui ui-monitor ui-monitor--capped" role="region" aria-label={`Preview monitor: ${playing.fileName}`} data-testid="footage-monitor">
          <div className="ui-monitor__frame ui-monitor__frame--16x9">
            <video key={playing.id} className="ui-contain" controls autoPlay poster={playing.thumbnailUrl || undefined}><source src={playing.playbackUrl} /></video>
          </div>
          <span className="ui-cluster">
            <Icon icon={Play} /><span className="ui-dense ui-truncate ui-grow">{playing.fileName}</span>
            <Button variant="quiet" icon={X} onClick={() => setPlayingClipId(null)}>Close player</Button>
          </span>
        </div>
      )}
      {open && (
        <Grid columns={columns} rows={rows} rowKey={c => c.id} density="medium" storageKey="hf-grid:production-footage" label="Raw footage on this production"
          selectedKey={playingClipId} onOpen={togglePlay} height={`${height}px`} />
      )}
      {facesClip && <FacesDialog clip={facesClip} cast={faceCast} chars={faceChars} onFaces={faces => onClipFaces?.(facesClip.id, faces)} onClose={() => setFacesFor(null)} />}
    </section>
  );
}
